import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sendNotification } from "@/lib/notifications.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Bell, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/notifications")({
  component: AdminNotificationsPage,
  head: () => ({ meta: [{ title: "Notifications — Admin" }] }),
});

function AdminNotificationsPage() {
  const qc = useQueryClient();
  const send = useServerFn(sendNotification);

  const [broadcast, setBroadcast] = useState(true);
  const [userId, setUserId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<"info" | "success" | "warning" | "error">("info");
  const [link, setLink] = useState("");

  const recent = useQuery({
    queryKey: ["admin", "notifications", "recent"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as any[];
    },
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      return send({
        data: {
          broadcast,
          user_id: broadcast ? undefined : userId || undefined,
          title,
          body: body || undefined,
          severity,
          link: link || undefined,
          type: "admin",
        },
      });
    },
    onSuccess: (r: any) => {
      toast.success(`Sent to ${r.sent} user${r.sent === 1 ? "" : "s"}`);
      setTitle("");
      setBody("");
      setLink("");
      qc.invalidateQueries({ queryKey: ["admin", "notifications", "recent"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Send failed"),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("notifications" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "notifications", "recent"] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-brand text-white">
          <Bell className="h-4 w-4" />
        </span>
        <div>
          <h1 className="text-xl font-semibold">Notifications</h1>
          <p className="text-sm text-muted-foreground">Send real-time alerts to users.</p>
        </div>
      </header>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-semibold">Broadcast to all users</Label>
            <p className="text-xs text-muted-foreground">Toggle off to target a specific user.</p>
          </div>
          <Switch checked={broadcast} onCheckedChange={setBroadcast} />
        </div>

        {!broadcast && (
          <div className="space-y-1.5">
            <Label>User ID</Label>
            <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="uuid of recipient" />
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New feature available" />
          </div>
          <div className="space-y-1.5">
            <Label>Severity</Label>
            <Select value={severity} onValueChange={(v) => setSeverity(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Message</Label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Optional details…" />
        </div>

        <div className="space-y-1.5">
          <Label>Link (optional)</Label>
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="/dashboard" />
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => sendMut.mutate()}
            disabled={!title || sendMut.isPending || (!broadcast && !userId)}
            className="gap-2"
          >
            <Send className="h-4 w-4" /> {sendMut.isPending ? "Sending…" : "Send notification"}
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-sm font-semibold mb-3">Recent notifications</h2>
        {recent.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (recent.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No notifications sent yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {recent.data!.map((n: any) => (
              <li key={n.id} className="flex items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{n.title}</span>
                    <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-secondary text-muted-foreground">{n.severity}</span>
                  </div>
                  {n.body && <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{n.body}</p>}
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    {new Date(n.created_at).toLocaleString()} · user {String(n.user_id).slice(0, 8)}…{n.read_at ? " · read" : " · unread"}
                  </p>
                </div>
                <button
                  onClick={() => delMut.mutate(n.id)}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-destructive transition"
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
